/**
 * Cross-tab slot registry: caps how many tabs of this app one browser profile may run at
 * once, and elects exactly one of them as the "leader" that owns the session-refresh
 * interval (see useSessionTimer.ts).
 *
 * <h3>Why localStorage and not the BFF session record</h3>
 * The cap is a resource guardrail, not a security boundary, and it could not be one:
 * an incognito window gets its own cookie, therefore its own BFF session, therefore its
 * own five slots — so enforcing on the server would buy authority it cannot actually
 * deliver, at the cost of a conditional DynamoDB write per tab per heartbeat and a
 * round-trip before any tab can render. localStorage is scoped to exactly the thing being
 * limited (one origin, one browser profile), needs no network, and makes the heartbeat
 * cheap enough to run every few seconds instead of riding the 15-minute refresh cadence —
 * which would have left every tab reading as dead for a third of every cycle, since the
 * TTL that frees a crashed tab's slot has to be shorter than the interval that renews it.
 *
 * <h3>One key per tab, not one shared blob</h3>
 * Every tab owns `bmo-inflow.tabslot.<tabId>` and writes only that key, so no tab can ever
 * clobber another's entry. The registry is the prefix scan of those keys. A single shared
 * object would make every write a read-modify-write of the whole thing, and localStorage has
 * no compare-and-swap to make that safe — under the ordering below a dropped write does not
 * just cost a heartbeat, it evicts the WRONG TAB.
 *
 * <h3>Admission is an ordering, not a count</h3>
 * Counting live tabs at write time cannot be made correct without a lock. Instead every tab
 * writes its own entry unconditionally and then derives its rank from the merged registry by
 * an order every tab computes identically, admitting itself only if that rank is below
 * MAX_TABS. Both sides of a race reach the same verdict with no lock.
 *
 * <h3>Three orderings, deliberately kept apart</h3>
 * - `lastSeen` — liveness only, restamped by the heartbeat. Drives the stale sweep.
 * - `lastActive` — when this tab was last VISITED. Drives admission: the least recently
 *   visited tab is the one pushed out when a new tab arrives.
 * - `seq` — a registry-local counter (not a timestamp, so a backwards clock step cannot
 *   reorder anyone). Drives leadership only.
 *
 * Two invariants hold this together, and both are load-bearing:
 *
 * 1. **The heartbeat must never stamp `lastActive`.** If it did, an evicted tab's next
 *    heartbeat would make it the most recently active tab, re-admitting it and evicting its
 *    replacement — which would do the same thing 15 seconds later, forever.
 *
 * 2. **Leadership must not follow the admission order.** useSessionTimer fires a FORCED
 *    refresh on assuming leadership, and a forced refresh deliberately skips the cross-tab
 *    coalescing check. If the leader were simply rank 0 of the admission order, leadership
 *    would move to whichever tab the user just looked at and fire a forced ISAM grant on
 *    every single tab switch. sessionRefreshLock.ts spells out where that ends: two grants
 *    for one rotating refresh token, `invalid_grant`, and the BFF deleting the session out
 *    from under every tab. Leadership therefore sorts on `seq`, which changes only when the
 *    leader actually goes away.
 */

import { v4 as uuidv4 } from 'uuid';

/**
 * This document's identity in the registry: minted once, then shared by every caller for the
 * life of the document.
 *
 * A module-level singleton and not per-caller state, because an id minted per CALLER is an id
 * that claims a second slot. claimTabSlot registers whatever id it is handed, so two callers
 * holding two uuids put two entries in the registry for one tab — and since the cap counts
 * entries, a browser with five workflow-editor tabs open spends all five slots on two and a
 * half tabs and evicts the rest. Anything that needs to say "this tab" — the slot cap itself,
 * the editor-lock registry — must read its id from here.
 */
let documentTabId = '';

export function getTabId(): string {
  if (!documentTabId) documentTabId = uuidv4();
  return documentTabId;
}

/** Per-tab key prefix. One entry per tab: `{ seq, lastSeen, lastActive }`. */
export const TAB_SLOT_PREFIX = 'bmo-inflow.tabslot.';

/**
 * The pre-LRU shared-blob key. Only still referenced so a build rolling out over tabs running
 * the previous build can clear it instead of leaving it parked in localStorage forever.
 */
const LEGACY_REGISTRY_KEY = 'bmo-inflow.tabs';

/** Maximum concurrent tabs per browser profile. */
export const MAX_TABS = 5;

/**
 * How often a tab restamps `lastSeen`. A local write, so this is nearly free — but it runs
 * forever on a form page, so it is not free enough to justify the sub-second cadence the
 * storage medium would allow.
 */
export const HEARTBEAT_MS = 15_000;

/**
 * How long a silent entry keeps its slot. Deliberately ~20 missed heartbeats rather than
 * two or three: browsers throttle background timers to roughly once a minute and freeze
 * fully-occluded tabs outright, so a tight window would evict tabs that are alive and hold
 * unsaved work. The cost of the slack is that a tab killed without running its `pagehide`
 * handler (crash, force-quit) parks its slot for up to this long; every ordinary close
 * releases immediately.
 *
 * It is also the leadership-handoff deadline: a frozen leader stops refreshing, and this
 * window has to be comfortably shorter than the `bmo_session` cookie's 20-minute Max-Age so
 * the next tab takes over long before the session lapses.
 */
export const STALE_MS = 5 * 60_000;

type TabEntry = { seq: number; lastSeen: number; lastActive: number };
type Registry = Record<string, TabEntry>;

export type SlotStatus = {
  admitted: boolean;
  /** True for the one admitted tab with the lowest `seq` — the one that owns the refresh interval. */
  isLeader: boolean;
};

const DENIED: SlotStatus = { admitted: false, isLeader: false };

/**
 * A tab's own `seq` and `lastActive`, remembered for the life of the document.
 *
 * Without this, a tab pruned while frozen would re-enter the registry as if newly opened —
 * taking a fresh `seq` (losing its leadership standing) and, worse, a fresh `lastActive`,
 * which would make a tab nobody has looked at in an hour rank as the most recently visited
 * and evict someone. Replaying the remembered values keeps a tab's place stable. A tab the
 * user genuinely returns to gets a fresh stamp from touchTabSlot instead.
 */
const knownEntry = new Map<string, { seq: number; lastActive: number }>();

function keyFor(tabId: string): string {
  return `${TAB_SLOT_PREFIX}${tabId}`;
}

/** Every live tab's entry, keyed by tabId. Malformed or unreadable entries are skipped. */
function read(): Registry {
  const out: Registry = {};
  let keys: string[];
  try {
    keys = Object.keys(localStorage);
  } catch {
    // Unavailable (private mode). An empty registry is the safe reading: it admits the tab
    // rather than locking the user out of an app they are entitled to use.
    return out;
  }

  for (const key of keys) {
    if (!key.startsWith(TAB_SLOT_PREFIX)) continue;
    const id = key.slice(TAB_SLOT_PREFIX.length);
    if (!id) continue;
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const entry = JSON.parse(raw) as Partial<TabEntry> | null;
      if (typeof entry?.seq !== 'number' || typeof entry?.lastSeen !== 'number') continue;
      out[id] = {
        seq: entry.seq,
        lastSeen: entry.lastSeen,
        // Entries written before lastActive existed rank on their liveness stamp, which is
        // the closest thing to "recently visited" those tabs ever recorded.
        lastActive: typeof entry.lastActive === 'number' ? entry.lastActive : entry.lastSeen,
      };
    } catch {
      // Corrupt (a half-written value, a key some other tool squatted on). Skipping one entry
      // is strictly better than the old whole-blob catch, which discarded every tab at once.
    }
  }
  return out;
}

function writeEntry(tabId: string, entry: TabEntry): void {
  try {
    localStorage.setItem(keyFor(tabId), JSON.stringify(entry));
  } catch {
    // Quota or private mode. The cap silently stops being enforced, which is the correct
    // way for a guardrail to fail — see read() above.
  }
}

function removeEntry(tabId: string): void {
  try {
    localStorage.removeItem(keyFor(tabId));
  } catch {
    // See writeEntry.
  }
}

/**
 * Drop entries whose tab has gone silent past STALE_MS, deleting the backing keys as we go.
 *
 * Deleting is what keeps crashed tabs from leaking keys forever. It is the one place a tab
 * writes to another tab's key, and it is safe precisely because the entry has already been
 * silent for twenty heartbeats.
 */
function sweep(registry: Registry, now: number, deleteStale: boolean): Registry {
  const live: Registry = {};
  for (const [id, entry] of Object.entries(registry)) {
    if (now - entry.lastSeen < STALE_MS) {
      live[id] = entry;
    } else if (deleteStale) {
      removeEntry(id);
    }
  }
  return live;
}

/**
 * The admission order: most recently visited first. The tail past MAX_TABS is evicted, so the
 * tab the user has gone longest without visiting is the one that gives up its slot.
 *
 * `seq` DESC breaks ties so that two tabs stamped in the same millisecond resolve toward the
 * newer one, and `tabId` breaks the remaining tie so every tab computes an identical order.
 */
function ranked(registry: Registry): string[] {
  return Object.entries(registry)
    .sort(
      ([idA, a], [idB, b]) =>
        b.lastActive - a.lastActive || b.seq - a.seq || (idA < idB ? -1 : idA > idB ? 1 : 0),
    )
    .map(([id]) => id);
}

/**
 * The leader: the longest-standing admitted tab. Separate from `ranked` on purpose — see
 * invariant 2 in the module comment.
 */
function leaderOf(registry: Registry): string | null {
  const admitted = new Set(ranked(registry).slice(0, MAX_TABS));
  let leader: string | null = null;
  let leaderSeq = Infinity;
  for (const [id, entry] of Object.entries(registry)) {
    if (!admitted.has(id)) continue;
    if (entry.seq < leaderSeq || (entry.seq === leaderSeq && leader !== null && id < leader)) {
      leader = id;
      leaderSeq = entry.seq;
    }
  }
  return leader;
}

function nextSeq(registry: Registry): number {
  return Object.values(registry).reduce((max, entry) => Math.max(max, entry.seq), 0) + 1;
}

function statusOf(registry: Registry, tabId: string): SlotStatus {
  const admitted = ranked(registry).indexOf(tabId) < MAX_TABS;
  return admitted ? { admitted: true, isLeader: leaderOf(registry) === tabId } : DENIED;
}

/**
 * Claim or renew this tab's slot, returning whether it may run and whether it leads.
 *
 * Idempotent — this is also the heartbeat, so it restamps liveness ONLY. Stamping
 * `lastActive` here would put two tabs into a permanent eviction loop (invariant 1).
 *
 * Safe to call on a tab that has already been refused: it re-tests admission, so a tab
 * recovers on its own the moment a slot frees. Unlike the pre-LRU version it does NOT delete
 * its own entry when refused — a refused tab has to stay in the registry to be ranked back in
 * later, and with one key per tab there is no shared blob for it to pollute.
 */
export function claimTabSlot(tabId: string): SlotStatus {
  const now = Date.now();
  const registry = sweep(read(), now, true);

  const remembered = knownEntry.get(tabId);
  const existing = registry[tabId];
  const seq = remembered?.seq ?? existing?.seq ?? nextSeq(registry);
  const lastActive = existing?.lastActive ?? remembered?.lastActive ?? now;

  knownEntry.set(tabId, { seq, lastActive });
  const entry: TabEntry = { seq, lastSeen: now, lastActive };
  registry[tabId] = entry;
  writeEntry(tabId, entry);

  return statusOf(registry, tabId);
}

/**
 * Mark this tab as just visited, then report its standing.
 *
 * This is the only writer of `lastActive`, and it is driven by visibility/focus transitions
 * rather than by keystrokes or clicks: the question the cap asks is "which tab has the user
 * gone longest without looking at", not "which tab was edited least recently". Tab switches
 * happen at human speed, so this needs no throttling.
 */
export function touchTabSlot(tabId: string): SlotStatus {
  const now = Date.now();
  const registry = sweep(read(), now, true);

  const remembered = knownEntry.get(tabId);
  const seq = remembered?.seq ?? registry[tabId]?.seq ?? nextSeq(registry);

  knownEntry.set(tabId, { seq, lastActive: now });
  const entry: TabEntry = { seq, lastSeen: now, lastActive: now };
  registry[tabId] = entry;
  writeEntry(tabId, entry);

  return statusOf(registry, tabId);
}

export type SlotInspection = SlotStatus & {
  /** False when this tab has no live entry — pruned while frozen, or never admitted. */
  present: boolean;
};

/**
 * Read this tab's standing WITHOUT writing anything.
 *
 * The read-only half matters more than it looks. Tabs watch the registry through the
 * `storage` event, which fires in every OTHER tab on each write — so if reacting to a
 * neighbour's write meant writing in turn, five tabs would amplify a single close into an
 * unbounded storm of events. Observers inspect; only the heartbeat, a visit, an explicit
 * retry, and a wake from freeze write.
 */
export function inspectTabSlot(tabId: string): SlotInspection {
  const registry = sweep(read(), Date.now(), false);
  if (!(tabId in registry)) return { ...DENIED, present: false };
  return { ...statusOf(registry, tabId), present: true };
}

/**
 * Drop this tab's slot so another tab can take it. Called on `pagehide`/`beforeunload` and
 * on unmount; the STALE_MS sweep is the backstop for the paths that never run one (crash,
 * force-quit, OS kill).
 *
 * The remembered `seq`/`lastActive` are deliberately NOT forgotten — a tab that releases and
 * re-claims (bfcache restore, a refused tab retrying) must keep its original standing.
 */
export function releaseTabSlot(tabId: string): void {
  removeEntry(tabId);
}

/**
 * Ids of every tab currently considered alive, by the same STALE_MS rule the slot cap uses.
 *
 * Exported for the editor-lock registry, which records which tabs have a workflow open and
 * must be able to tell a tab that closed from one that crashed — a crashed tab that kept its
 * name in that list forever would pin a workflow lock until the TTL. Read-only, like
 * {@link inspectTabSlot} and for the same reason: observers must not write.
 */
export function liveTabIds(): string[] {
  return Object.keys(sweep(read(), Date.now(), false));
}

/**
 * Clear the pre-LRU shared-blob key. Tabs on the old build and tabs on this one cannot see
 * each other's slots either way, so the cap may briefly be exceeded during a rollout; this
 * just stops the dead key sitting in localStorage once those tabs are gone.
 */
export function clearLegacyRegistry(): void {
  try {
    localStorage.removeItem(LEGACY_REGISTRY_KEY);
  } catch {
    // See writeEntry.
  }
}

/** Test seam — the ordering rules above are the whole point of the module. Not for app code. */
export const tabRegistryInternals = {
  read,
  ranked,
  leaderOf,
  resetKnownSeq: () => knownEntry.clear(),
  resetTabId: () => {
    documentTabId = '';
  },
};
